import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  CssBaseline,
  Box,
  Divider,
  Avatar,
  Switch,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../config/AuthContext";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import splogo from "../../assets/splogo.png";
import { splitName } from "../../utils/name";
import MainLinks from "./MainLinks";
import useData from "./hooks/useData";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import Brightness4Icon from "@mui/icons-material/Brightness4";

const drawerWidth = 240;

const ResponsiveAppBar = ({ darkMode, setDarkMode }) => {
  const { currentUser, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userAvatar, setUserAvatar] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const navigate = useNavigate();
  const { fetchSchoolinfo } = useData();

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          const userDoc = await getDoc(doc(db, "users", currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setUserName(
              userData.firstName || splitName(userData.name).firstName,
            );

            if (userData.ccpSchoolFlag === true) {
              const schoolInfo = await fetchSchoolinfo(userData.school_id);
              setUserAvatar(schoolInfo.school_logo);
              setSchoolName(schoolInfo.school_name);
            } else if (userData.techPrepAdminFlag || userData.ccpAdminFlag) {
              setUserAvatar(userData.avatar);
              setSchoolName("Admin"); // Or some default for admins
            } else {
              setUserAvatar(userData.avatar);
              setSchoolName("User"); // Or some default for regular users
            }
          } else {
            console.error("No such document!");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    fetchUserData();
  }, [currentUser, fetchSchoolinfo]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  const handleThemeChange = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
    },
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to logout:", error);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            bgcolor: "#ac1a2f",
          }}
        >
          <Toolbar>
            {currentUser && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {/* A real link (not an onClick navigate) so Ctrl/Cmd-click,
                middle-click and "Open in new tab" work like any other link */}
            <Link to="/dashboard" style={{ display: "flex" }}>
              <img
                src={splogo}
                alt="Logo"
                style={{
                  marginRight: "10px",
                  width: "auto",
                  height: "40px",
                  cursor: "pointer",
                }}
              />
            </Link>
            {currentUser && (
              <Box
                sx={{
                  flexGrow: 1, // Added to make the welcome message take up available space
                  display: "flex",
                  justifyContent: "center", // Center the welcome message
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ color: "white" }}
                >
                  {schoolName && `Welcome ${schoolName}`}
                </Typography>
              </Box>
            )}
            {currentUser && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Switch
                  checked={darkMode}
                  onChange={handleThemeChange}
                  icon={<Brightness7Icon />}
                  checkedIcon={<Brightness4Icon />}
                  color="default"
                />
                {userAvatar && (
                  <Avatar
                    component={Link}
                    to="/dashboard"
                    src={userAvatar}
                    alt={userName}
                    sx={{
                      width: 40,
                      height: 40,
                      mr: 2,
                      cursor: "pointer",
                    }}
                  />
                )}
                <Typography variant="h6" component="div" sx={{ mr: 2 }}>
                  {userName}
                </Typography>
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="logout"
                  onClick={handleLogout}
                  color="inherit"
                >
                  <LogoutIcon />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </AppBar>
        {currentUser && (
          <Drawer
            variant="temporary"
            open={drawerOpen}
            onClose={handleDrawerToggle}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: drawerWidth,
                boxSizing: "border-box",
              },
            }}
          >
            <Toolbar />
            <Divider />
            <MainLinks />
          </Drawer>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ResponsiveAppBar;
